import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'
import { PageIdWoofzPupBl } from './constants/pages'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const isEnLocale = () => {
  const langFromUrl = new URL(window.location.href).searchParams.get('lang')

  return (
    (langFromUrl || useZustandSessionStore.getState().language) === LOCALES.EN
  )
}

const isGoalQuestionEn = () => {
  const langFromUrl = new URL(window.location.href).searchParams.get('lang')

  return langFromUrl === LOCALES.EN
}

export const getXStateConfigWoofzPupBl = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !hasEmail &&
            !hasSubscription &&
            !hasAccount &&
            hasAnswers &&
            pageIdFromUrl !== PageIdWoofzPupBl.NEWSLETTER_URGE_PUP_BL
          )
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return pageIdFromUrl === PageIdWoofzPupBl.NEWSLETTER_URGE_PUP_BL
        },
        target: PageIdWoofzPupBl.EMAIL_PUP_BL,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageIdWoofzPupBl.DOWNLOAD_PUP_BL,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageIdWoofzPupBl.SUBSCRIPTIONS_PUP_BL,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.SUBSCRIPTIONS_PUP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_PUP_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_PUP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.UPSELL_PUP_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_PUP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.UPSELL_FEEDING_GUIDE_PUP_BL,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_PUP_BL && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_PUP_BL,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_PUP_BL && {
            NEXT_FLOW_STEP: PageId.ACCOUNT_NOVE8,
          }),
          ...(page.pageId === PageId.URGE_PAGE_4_PUP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isGoalQuestionEn,
                target: PageId.TRAINING_PLACE_PUP_BL,
              },
              {
                target: PageId.PARENT_GOAL_PUP_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.TRAINING_PLACE_PUP_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: isGoalQuestionEn,
                target: PageId.URGE_PAGE_4_PUP_BL,
              },
              {
                target: PageId.PARENT_GOAL_PUP_BL,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_NOVE8]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_PUP_BL,
    },
  },
  [PageId.DOWNLOAD_PUP_BL]: {
    type: 'final',
  },
})
